import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import Npc from './Npc';
import SafeMount from './SafeMount';
import CameraBasisProbe from './CameraBasisProbe';
import { api } from '../services/api';
import { useWorldStore } from '../state/worldStore';

// NpcManager — populates the street with featured-artist residents (Spec: City
// Life). Data-driven: models/avatars/roster.json is the MODEL SET (which artists
// have a rigged body + where they stand); /api/featured-artists/showcase
// (is_featured_artist) supplies live names/genres and validates the roster.
//
// A featured artist with no roster entry is logged as "needs a model" so the
// content pipeline knows what to build next. Roster JSON is fetched SAME-ORIGIN
// (a Vite public asset), not through the API base.
const SHOW_TEST = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('test') === '1';

export default function NpcManager() {
  const [npcs, setNpcs] = useState([]);
  const setNpcList = useWorldStore((s) => s.setNpcList);
  const setFollowing = useWorldStore((s) => s.setFollowing);
  const setOwned = useWorldStore((s) => s.setOwned);
  const embodied = useWorldStore((s) => s.embodied); // avatars a member is driving
  const placing = useWorldStore((s) => s.adminTool) === 'place';
  // Saved street placements (world_objects, zone 'street'). Read from the STORE
  // rather than a local fetch so an edit in the panel moves the body on the
  // street immediately instead of after a reload.
  const placed = useWorldStore((s) => s.worldObjects);
  // The fetch that used to sit here belongs to StreetStream now — one loader for
  // the streamed street kinds, asking by cell instead of pulling every resident
  // in the world. Reading from the store is unchanged, which is what keeps a
  // panel edit moving the body on the street immediately.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // no-store: on returning to the street this re-reads the CURRENT file
      // (autosaved sizes/positions), not a stale browser-cached copy.
      const roster = await fetch('/models/avatars/roster.json', { cache: 'no-store' })
        .then((r) => r.json())
        .catch(() => ({ npcs: [] }));
      let featured = [];
      try { featured = (await api.featuredArtists())?.artists || []; } catch { /* offline → manifest only */ }

      // Who the viewer follows + owns (for the map's headshot pins). Optional —
      // a signed-out / offline viewer just gets no markers.
      try {
        const follows = (await api.following())?.following || [];
        if (!cancelled) setFollowing(follows.map((f) => f.avatar_id));
      } catch { if (!cancelled) setFollowing([]); }
      try {
        const mine = (await api.myAvatars())?.avatars || [];
        if (!cancelled) setOwned(mine.map((a) => a.avatar_id));
      } catch { if (!cancelled) setOwned([]); }

      // Where members LEFT their driven avatars in the city (persistence) → place
      // the autonomous NPC there, overriding the roster.json default.
      let saved = [];
      try { saved = (await api.worldPositions())?.positions || []; } catch { /* none yet */ }
      const savedById = new Map(saved.map((p) => [p.avatar_id, p]));

      const byId = new Map(featured.map((a) => [a.avatar_id, a]));
      const entries = roster.npcs || [];
      const modeledIds = new Set(entries.map((n) => n.avatarId));

      // Enrich each modeled NPC with live name/genre + headshot from the
      // featured list (headshot powers the map's follow markers).
      const merged = entries.map((n) => {
        const a = byId.get(n.avatarId);
        const sp = savedById.get(n.avatarId);
        return {
          ...n,
          // Saved city position wins over the roster default (keep the roster Y).
          post: sp ? [sp.x, (Array.isArray(n.post) ? n.post[1] : 0) || 0, sp.z] : n.post,
          rotationY: sp?.heading != null ? sp.heading : n.rotationY,
          name: a?.name || n.name || `Artist ${n.avatarId}`,
          genre: a?.main_genre || null,
          headshotUrl: a?.profile_image_url || null,
        };
      });

      // Surface featured artists still waiting on a rigged model.
      const needsModel = featured.filter((a) => !modeledIds.has(a.avatar_id));
      if (needsModel.length) {
        console.log(`[NPC] ${merged.length} modeled · ${needsModel.length} featured artist(s) need a model:`,
          needsModel.map((a) => `${a.name} (#${a.avatar_id})`).join(', '));
      }
      if (!cancelled) { setNpcs(merged); setNpcList(merged); }
    })();
    return () => { cancelled = true; };
  }, [setNpcList]);

  const placedBySlug = useMemo(
    () => new Map((placed || []).filter((p) => p.zone_id === 'street' && p.kind === 'resident').map((p) => [p.ref, p])),
    [placed],
  );

  if (!npcs.length) return null;
  // Hide the autonomous NPC for any avatar a member is currently embodying — the
  // player-driven figure stands in, so the world shows exactly one of them.
  // Roster entries marked `test: true` are A/B bodies for comparing pipeline
  // output side by side (same plates, different reconstructor). They are not
  // residents and must never show up in a normal session — ?test=1 opts in.
  const visible = npcs.filter((n) => (SHOW_TEST || !n.test) && !embodied.includes(n.avatarId))
    // Saved placement wins over the roster default, applied HERE at render
    // rather than in the load effect — the panel patches store state as you
    // nudge, and this is what turns that into the body actually moving.
    .map((n) => {
      const p = placedBySlug.get(n.slug);
      return p ? { ...n, post: p.pos, rotationY: p.rotY, height: p.size } : n;
    });
  return (
    <Suspense fallback={null}>
      {placing && <CameraBasisProbe />}
      {/* One NPC per boundary. Roster entries can point at assets the pipeline
          is STILL RENDERING (rigged + walk exported, idle not yet), and a
          missing GLB throws inside the loader — unwrapped, that one entry
          unmounts the whole Canvas and drops the WebGL context. Retrying lets
          an avatar appear by itself once its clips finish. */}
      {visible.map((n) => (
        <SafeMount
          key={n.avatarId ?? n.slug}
          label={`NPC ${n.name || n.slug}`}
          retryMs={30000}
          onRetry={() => useGLTF.clear([n.rigged, n.walk, n.idle].filter(Boolean))}
        >
          <Npc npc={n} />
        </SafeMount>
      ))}
    </Suspense>
  );
}
